/**
 * 홈 화면 왼쪽 절반, "오늘의 문제" 박스 2개(객관식·서술형)를 세로로 쌓아 보여준다.
 * StudentHome 이 useGetProblemPreview(객관식)·useGetEssayPreview(서술형) 응답을 그대로 넘긴다.
 *
 * 박스 클릭 시 바로 라우팅하지 않고 useExamEntryGate 의 startChapter 를 거친다.
 * 객관식은 진입 직전 코드 입력(requiresCode) 여부를 서버에 물어봐 필요하면 `/exam/enter`로
 * 먼저 튕긴다. 서술형은 이 단계에서 게이트를 조회하지 않는다(examRef 미전달) — 튜토리얼을
 * 먼저 보여준 뒤 첫 미션 진입 직전에 `play.tsx`가 직접 게이트를 물어본다.
 */
import React, { useMemo } from "react";
import Image from "next/image";
import ArrowRight from "../icons/ArrowRight";
import ProgressCard from "./ProgressCard";
import HorizontalLine from "../icons/HorizontalLine";
import Flag from "../icons/home/Flag";
import { ProblemPreviewProps } from "@/fetcher/useGetProblemPreview";
import { EssayPreviewResponse } from "@/fetcher/essay-learning/types";
import { useId } from "@/hooks/useId";
import { useExamEntryGate } from "@/hooks/useExamEntryGate";

// 한 박스(트랙)의 4가지 상태. 객관식·서술형 각자 독립 결정.
//  - previous   : 할당됐고 한 번도 풀지 않음 → bg-primary-400  (파랑, 클릭 가능)
//  - in-progress: 일시정지/진행중 (이어풀기)  → bg-accent (Star Amber, 클릭 가능). 글자는 Deep Ink — 앰버 위 흰 글자 금지(2.2:1)
//                 화살표 원은 Deep Ink 채움 + 앰버 화살표로 뒤집어 앰버 면 위에서 보이게 한다 (2026-09-06 사용자 결정)
//  - completed  : 오늘 다 풀었음             → bg-fill-strong  (회색, 클릭 비활성)
//  - empty      : 할당된 문제 자체가 없음    → bg-fill-strong  (회색, 클릭 비활성)
export type BoxStatus = "previous" | "in-progress" | "completed" | "empty";

type BoxKind = "objective" | "essay";

const PROBLEM_ROUTE = "/problem";
const ESSAY_ROUTE = "/essay-learning/play";

interface Props {
  previewData: ProblemPreviewProps | undefined;
  essayPreview: EssayPreviewResponse | undefined;
}

/** 오늘의 객관식·서술형 문제 박스 2개. 두 응답이 모두 아직 안 왔을 때만 null 을 반환해 잠깐 숨긴다. */
const TodayProblem: React.FC<Props> = ({ previewData, essayPreview }) => {
  // 검사 챕터 시작 게이트. 진입 직전 requiresCode 판정.
  const { startChapter } = useExamEntryGate();
  const objectiveLabelId = useId(undefined, "today-problem-objective-label");
  const essayLabelId = useId(undefined, "today-problem-essay-label");

  // 객관식 상태: 할당없음 > 완료 > 진행중 > 미시작 순으로 우선 판정.
  // questionTotalCount가 0/undefined/누락 어느 쪽이든 "할당 없음"으로 묶어 falsy 체크.
  const objectiveStatus = useMemo<BoxStatus>(() => {
    if (!previewData || !previewData.questionTotalCount) return "empty";
    if (previewData.completedLastChapterDate) return "completed";
    if (previewData.hasTempSave) return "in-progress";
    if ((previewData.completedQuestionCount ?? 0) > 0) return "in-progress";
    return "previous";
  }, [previewData]);

  // 서술형 상태: submitDate(완료) > hasTempSave/진척(진행중) > 미시작.
  // questionCount falsy 묶음. null/0/undefined 어느 쪽으로 와도 "할당 없음"으로.
  const essayStatus = useMemo<BoxStatus>(() => {
    if (!essayPreview || !essayPreview.questionCount) return "empty";
    if (essayPreview.submitDate) return "completed";
    if (essayPreview.hasTempSave) return "in-progress";
    if ((essayPreview.completedQuestionCount ?? 0) > 0) return "in-progress";
    return "previous";
  }, [essayPreview]);

  // 완료 도장(completedLastChapterDate)이 박힌 즉시 100% 강제. BE가 completedQuestionCount를
  // 동기화 안 줘도 시각 표현이 의미와 어긋나지 않도록.
  const objectiveProgress = useMemo<number>(() => {
    if (!previewData) return 0;
    if (previewData.completedLastChapterDate) return 100;
    if (!previewData.questionTotalCount) return 0;
    return Math.round(
      (previewData.completedQuestionCount / previewData.questionTotalCount) *
        100
    );
  }, [previewData]);

  // 서술형은 미션(시나리오) 단위로 센다. 점 7개 = 시작점 + 미션 6개, 미션 하나 완료 = 점 하나.
  // 문항 수 기준(2/12 = 17%)으로 재면 미션 1을 끝내도 첫 점(1/6)에 못 미쳐 QA 에서 위치가 어긋났다.
  // 구 BE 응답(scenarioCount 없음)이면 문항 수 기준으로 폴백한다.
  const essayProgress = useMemo<number>(() => {
    if (!essayPreview) return 0;
    if (essayPreview.submitDate) return 100;
    if (essayPreview.scenarioCount) {
      return Math.round(
        ((essayPreview.completedScenarioCount ?? 0) / essayPreview.scenarioCount) * 100
      );
    }
    if (!essayPreview.questionCount) return 0;
    return Math.round(
      ((essayPreview.completedQuestionCount ?? 0) / essayPreview.questionCount) *
        100
    );
  }, [essayPreview]);

  // 두 SWR 응답이 모두 미도착(undefined)일 때만 잠깐 대기.
  // 한쪽이라도 settle(데이터 또는 null) 됐으면 박스 노출. empty 상태로라도 화면을 보여줘 사용자가 비어 있음을 인지하게 함.
  // 기존 `||` 가드는 한쪽 API가 빈 응답으로 영원히 undefined인 사용자에서 박스 전체를 사라지게 만들었다.
  if (previewData === undefined && essayPreview === undefined) return null;

  return (
    <div className="w-1/2 flex flex-col gap-y-[20px]">
      <TodayProblemBox
        kind="objective"
        labelId={objectiveLabelId}
        status={objectiveStatus}
        progress={objectiveProgress}
        chapterName={previewData?.chapterName ?? ""}
        questionTotalCount={previewData?.questionTotalCount ?? 0}
        completedQuestionCount={previewData?.completedQuestionCount ?? 0}
        onClick={() =>
          void startChapter({
            examType: "OBJECTIVE",
            examRef: previewData?.chapterSetId,
            destination: PROBLEM_ROUTE,
          })
        }
      />
      <TodayProblemBox
        kind="essay"
        labelId={essayLabelId}
        status={essayStatus}
        progress={essayProgress}
        chapterName={essayPreview?.chapterName ?? ""}
        questionTotalCount={essayPreview?.questionCount ?? 0}
        completedQuestionCount={essayPreview?.completedQuestionCount ?? 0}
        progressSteps={essayPreview?.scenarioCount}
        onClick={() => {
          if (!essayPreview?.essayChapterId) return;
          // 서술형은 홈 단계에서 게이트를 조회하지 않는다(examRef 미전달 → startChapter가
          // 기존 fail-open 분기로 곧장 이동). 튜토리얼을 먼저 보여준 뒤 첫 미션 진입
          // 직전에 play.tsx가 직접 게이트를 물어보므로, essayChapterTemplateId는
          // templateId 쿼리로만 넘겨 그때 쓰게 한다(2026-09-20 사용자 결정).
          const templateQuery =
            essayPreview.essayChapterTemplateId != null
              ? `&templateId=${essayPreview.essayChapterTemplateId}`
              : "";
          void startChapter({
            examType: "ESSAY",
            examRef: undefined,
            destination: `${ESSAY_ROUTE}?chapterId=${essayPreview.essayChapterId}${templateQuery}`,
            // 풀이 화면은 전체 화면이 원칙. 재접속 때도 이 클릭이 전체 화면 요청의 제스처가 된다.
            fullscreen: true,
          });
        }}
      />
    </div>
  );
};

interface BoxProps {
  kind: BoxKind;
  labelId: string;
  status: BoxStatus;
  progress: number;
  chapterName: string;
  questionTotalCount: number;
  completedQuestionCount: number;
  /** 진행 막대 체크포인트 구간 수. 서술형은 미션 수. 미지정 시 ProgressBar 기본값. */
  progressSteps?: number;
  onClick: () => void;
}

const TITLE_TEXT: Record<BoxKind, string> = {
  objective: "오늘의 객관식 문제",
  essay: "오늘의 서술형 문제",
};

const HEADLINE: Record<BoxStatus, string> = {
  previous: "새로운 문제가 기다리고 있어요!",
  "in-progress": "이어서 풀어보아요!",
  // 비-인터랙티브 두 상태(completed/empty)는 사용자 입장에서 "오늘 더 풀 문제 없음" 으로 의미 동일 → 멘트 통일.
  completed: "오늘은 할당된 문제가 없어요",
  empty: "오늘은 할당된 문제가 없어요",
};

const BG_CLASS: Record<BoxStatus, string> = {
  previous: "bg-primary-400",
  "in-progress": "bg-accent",
  completed: "bg-fill-strong",
  empty: "bg-fill-strong",
};

// 상태 텍스트. 타이틀 뒤에 붙여 사용자/스크린리더 동시 인지.
const STATUS_LABEL: Record<BoxStatus, string> = {
  previous: "미시작",
  "in-progress": "진행중",
  // HEADLINE과 동일 사유. 비-인터랙티브 두 상태 라벨 통일.
  completed: "할당된 문제 없음",
  empty: "할당된 문제 없음",
};

// 트랙별 캐릭터 이미지. ProgressBar 끝점 인디케이터 + empty 안내 카드에 동일 사용.
const CHARACTER_SRC: Record<BoxKind, string> = {
  objective: "/images/char_biyak.png",
  essay: "/images/char_coding.png",
};

const TodayProblemBox: React.FC<BoxProps> = ({
  kind,
  labelId,
  status,
  progress,
  chapterName,
  questionTotalCount,
  completedQuestionCount,
  progressSteps,
  onClick,
}) => {
  const isInteractive = status === "previous" || status === "in-progress";

  // 글자색은 배경 짝에 따른다: 인디고 채움 → 흰색, 앰버 채움 → Deep Ink(heading), 회색 → 중립.
  const titleClass =
    status === "previous"
      ? "text-20 text-white font-bold"
      : status === "in-progress"
        ? "text-20 text-heading font-bold"
        : "text-20 text-strong font-extrabold";
  const headlineClass =
    status === "previous"
      ? "text-15 text-white font-normal"
      : status === "in-progress"
        ? "text-15 text-heading font-normal"
        : "text-15 text-body font-normal";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!isInteractive}
      aria-disabled={!isInteractive}
      aria-labelledby={labelId}
      data-status={status}
      data-kind={kind}
      className={[
        "problem-card text-left",
        BG_CLASS[status],
        isInteractive ? "cursor-pointer" : "cursor-not-allowed",
      ].join(" ")}
    >
      <div className="flex flex-col gap-y-[10px] mb-[24px]">
        <div id={labelId} className={titleClass}>
          {`${TITLE_TEXT[kind]} · ${STATUS_LABEL[status]}`}
        </div>
        <div className={headlineClass}>{HEADLINE[status]}</div>

        {status === "previous" && <ArrowRight />}
        {status === "in-progress" && (
          <div className="flex">
            <div className="bg-heading arrow-circle [&_svg_path]:fill-accent">
              <ArrowRight />
            </div>
          </div>
        )}
      </div>

      {/* 비-인터랙티브 두 상태(completed/empty) 모두 placeholder 카드로 통일.
          ProgressCard는 사용자가 진짜로 풀 수 있는(또는 풀던) 트랙에서만 노출. 의미 충돌 방지. */}
      {isInteractive ? (
        <ProgressCard
          chapter={chapterName}
          totalQuestions={questionTotalCount}
          progress={progress}
          characterSrc={CHARACTER_SRC[kind]}
          steps={progressSteps}
        />
      ) : (
        <div className="flex flex-col gap-y-[16px] bg-white rounded-[12px] py-[12px] pl-[15px] pr-[25px]">
          <div className="flex items-center justify-between">
            <div className="text-20 font-bold text-body font-heading">다음 챕터</div>
            <div className="rounded-[20px] px-[10px] py-[7px] bg-fill">
              <span className="text-14 font-medium text-body">준비 중</span>
            </div>
          </div>

          <HorizontalLine />

          <div className="relative pt-[40px]">
            <div className="relative w-full h-[8px] rounded-full bg-fill-strong">
              <div
                className="progress-circle bg-disabled"
                style={{ left: "0%" }}
              />
              <div
                className="progress-circle bg-disabled"
                style={{ left: "20%" }}
              />
              <div
                className="progress-circle bg-disabled"
                style={{ left: "40%" }}
              />
              <div
                className="progress-circle bg-disabled"
                style={{ left: "60%" }}
              />
              <div
                className="progress-circle bg-disabled"
                style={{ left: "80%" }}
              />
              <div className="progress-circle left-full bg-disabled" />
              <Image
                src={CHARACTER_SRC[kind]}
                alt=""
                width={37}
                height={41}
                className="absolute bottom-[7px] z-40"
                style={{ left: "calc(100% - 14px)" }}
              />
              <div className="absolute -top-6 left-full">
                <Flag />
              </div>
            </div>
            <div className="text-14 text-body font-medium pt-[9px]">
              다음 챕터를 기다리고 있어요
            </div>
          </div>
        </div>
      )}
    </button>
  );
};

export default TodayProblem;
